import React from "react";
import { NavLink, useNavigate, Outlet, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Package, Plus, Upload, MapPin, Key, Users, Settings, LogOut, Building2, ClipboardList, PackageX, Receipt, Leaf, Navigation, FileText, BookOpen, ShieldAlert } from "lucide-react";
import { businessApi } from "../../lib/api";

const navItems = [
  { to: "/business/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "biz-nav-dashboard" },
  { to: "/business/shipments", label: "Forsendelser", icon: Package, testid: "biz-nav-shipments" },
  { to: "/business/live-tracking", label: "Live tracking", icon: Navigation, testid: "biz-nav-live-tracking" },
  { to: "/business/create", label: "Opret forsendelse", icon: Plus, testid: "biz-nav-create" },
  { to: "/business/import", label: "CSV/Excel import", icon: Upload, testid: "biz-nav-import" },
  { to: "/business/manifests", label: "Pickup-manifests", icon: ClipboardList, testid: "biz-nav-manifests" },
  { to: "/business/failed-and-returns", label: "Fejl & returer", icon: PackageX, testid: "biz-nav-failed-returns" },
  { to: "/business/invoice", label: "Faktura-grundlag", icon: Receipt, testid: "biz-nav-invoice" },
  { to: "/business/billing", label: "Fakturaer", icon: FileText, testid: "biz-nav-billing" },
  { to: "/business/claims", label: "Claims", icon: PackageX, testid: "biz-nav-claims" },
  { to: "/business/rate-card", label: "Prisliste", icon: Receipt, testid: "biz-nav-rate-card" },
  { to: "/business/terms", label: "Betingelser", icon: BookOpen, testid: "biz-nav-terms" },
  { to: "/business/esg", label: "ESG / CO2e", icon: Leaf, testid: "biz-nav-esg" },
  { to: "/business/pickup-locations", label: "Pickup-lokationer", icon: MapPin, testid: "biz-nav-pickup-locations" },
  { to: "/business/api", label: "API & Webhooks", icon: Key, testid: "biz-nav-api" },
  { to: "/business/users", label: "Brugere", icon: Users, testid: "biz-nav-users" },
  { to: "/business/settings", label: "Indstillinger", icon: Settings, testid: "biz-nav-settings" },
];

export default function BusinessLayout() {
  const nav = useNavigate();
  const [user, setUser] = React.useState(null);
  const [customer, setCustomer] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const loadMe = React.useCallback(async () => {
    try {
      const { data } = await businessApi().get("/business/auth/me");
      setUser(data.user);
      setCustomer(data.customer);
      localStorage.setItem("ema_business_user", JSON.stringify(data.user));
      localStorage.setItem("ema_business_customer", JSON.stringify(data.customer));
    } catch {
      localStorage.removeItem("ema_business_token");
      nav("/business/login", { replace: true });
    } finally { setLoading(false); }
  }, [nav]);

  React.useEffect(() => {
    if (!localStorage.getItem("ema_business_token")) { nav("/business/login", { replace: true }); return; }
    loadMe();
  }, [loadMe, nav]);

  const logout = () => {
    localStorage.removeItem("ema_business_token");
    localStorage.removeItem("ema_business_user");
    localStorage.removeItem("ema_business_customer");
    toast.success("Logget ud");
    nav("/business/login", { replace: true });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-brand-text2">Indlæser portal...</div>;
  if (user?.must_change_password && window.location.pathname !== "/business/change-password") {
    return <Navigate to="/business/change-password" replace/>;
  }

  return (
    <div className="min-h-screen bg-brand-bg2 flex" data-testid="business-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-brand flex flex-col">
        <div className="p-5 border-b border-brand">
          <div className="flex items-center gap-2 text-brand-dark">
            <Building2 size={20}/>
            <span className="font-display font-bold text-lg">EMA Business</span>
          </div>
          <div className="text-xs text-brand-text2 mt-2 truncate" data-testid="biz-current-customer">{customer?.company_name}</div>
          {customer?.cvr && <div className="text-xs text-brand-text2">CVR {customer.cvr}</div>}
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {navItems.map((n) => (
            n.disabled ? (
              <div key={n.to} className="px-4 py-2 flex items-center gap-2 text-sm text-brand-text2/50 cursor-not-allowed" title="Tilgængelig i kommende sub-sprint">
                <n.icon size={16}/> {n.label} <span className="ml-auto text-[10px] uppercase bg-gray-100 px-1.5 py-0.5">snart</span>
              </div>
            ) : (
              <NavLink key={n.to} to={n.to} data-testid={n.testid}
                className={({ isActive }) =>
                  `px-4 py-2 flex items-center gap-2 text-sm border-l-4 ${isActive ? "border-brand-red bg-brand-bg2 text-brand-dark font-semibold" : "border-transparent text-brand-text2 hover:bg-brand-bg2 hover:text-brand-dark"}`
                }>
                <n.icon size={16}/> {n.label}
              </NavLink>
            )
          ))}
        </nav>
        <div className="p-3 border-t border-brand">
          <div className="text-xs text-brand-text2 mb-1 truncate">{user?.email}</div>
          <div className="text-[10px] text-brand-text2 mb-2">{user?.role === "owner" ? "Ejer" : "Bruger"}</div>
          <button onClick={logout} className="w-full text-sm text-red-600 hover:text-red-800 flex items-center gap-1" data-testid="biz-logout">
            <LogOut size={14}/> Log ud
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-x-auto p-8">
        <BookingGateBanner/>
        <Outlet context={{ user, customer, reload: loadMe }}/>
      </main>
    </div>
  );
}

function BookingGateBanner() {
  const [gate, setGate] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await businessApi().get("/b2b/2r5/booking-gates");
        setGate(data);
      } catch { /* silent */ }
    })();
  }, []);
  if (!gate || gate.ok) return null;
  return (
    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="booking-gate-banner">
      <div className="flex items-start gap-2">
        <ShieldAlert className="text-amber-700 flex-shrink-0 mt-0.5" size={18}/>
        <div className="flex-1">
          <div className="font-bold text-amber-900 mb-1">Booking blokeret indtil følgende er løst:</div>
          <ul className="list-disc list-inside text-amber-800 space-y-0.5">
            {(gate.blocking_reasons || []).map((r, i) => <li key={i} data-testid={`gate-reason-${i}`}>{r}</li>)}
          </ul>
          <div className="mt-2 flex gap-2 flex-wrap">
            {!gate.gate_state?.terms_accepted && (
              <a href="/business/terms" className="text-xs px-3 py-1 rounded bg-amber-600 text-white font-semibold" data-testid="gate-link-terms">Acceptér betingelser</a>
            )}
            {!gate.gate_state?.rate_card_active && (
              <a href="/business/rate-card" className="text-xs px-3 py-1 rounded bg-amber-600 text-white font-semibold" data-testid="gate-link-rate-card">Se prisliste</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
